/* eslint-disable @typescript-eslint/ban-ts-comment */
import { type InteractionSettings } from '@components/Presentation/types';
import { useState, useEffect } from 'react';
import Bar from './bar';
import { type SAFonts } from '@atoms/FontPicker';
import useFonts from '@lib/hooks/useFonts';
import { usePathname } from 'next/navigation';

interface User {
  username: string;
}
interface RatingPollsChartProps {
  origin: 'EDIT_PRESENTATION' | 'LIVE_PRESENTATION' | 'RESULT'
  data: {
    contribution: {
      percentage: number;
      body: string;
      option_number?: number;
      totalUsers?: number;
      count?: number;
      users?: User[];
    }[];
    averageRating: string;
  };
  colors: any;
  settings: InteractionSettings;
  fontStyle?: SAFonts;
  fontSizeScale?: number | string;
}

const RatingPollsChart = ({ data, colors, settings, origin, fontSizeScale = 1, fontStyle }: RatingPollsChartProps) => {
  const [contribution, setContribution] = useState(data?.contribution);

  const pathname = usePathname();

  const { getFontStyle } = useFonts();
  const font = settings?.FontStyle;
  const fontFamily = pathname?.includes('/themes') ? fontStyle ?? getFontStyle(font as SAFonts) : getFontStyle(font as SAFonts);
  const fontScale = pathname?.includes('/themes') ? (fontSizeScale as any).ratingPollsFontSize : Number(settings?.ratingPollsFontSize);
  const textColor = colors?.ratingPollsLabelColor || colors?.ratingPollsTextColor || '#111111';
  useEffect(() => {
    if (!data?.contribution) return;

    const targetScale = parseInt(settings?.ratingPollScale || '5', 10);
    const currentLength = data.contribution.length;

    if (currentLength === targetScale) {
      setContribution(data.contribution);
    } else if (currentLength < targetScale) {
      const newContribution = [...data.contribution];

      for (let i = currentLength + 1; i <= targetScale; i++) {
        newContribution.push({
          option_number: i,
          percentage: 0,
          count: 0,
          body: ` ${i}`,
          users: []
        });
      }

      setContribution(newContribution);
    } else {
      const newContribution = data.contribution.slice(0, targetScale);
      setContribution(newContribution);
    }
  }, [settings?.ratingPollScale, data?.contribution]);

  const styles = {
    EDIT_PRESENTATION: {
      average: 'text-white text-2xl font-semibold mb-4',
      label: 'text-white text-xl font-semibold mt-2',
      gap: settings?.ratingPollScale === '5' ? 'gap-10' : 'gap-3',
      avgFontSize: 28
    },
    LIVE_PRESENTATION: {
      average: 'text-white text-[32px] font-semibold mb-10',
      label: 'text-white font-semibold mt-2 text-3xl',
      gap: settings?.ratingPollScale === '5' ? 'gap-10' : 'gap-3',
      avgFontSize: 32
    },
    RESULT: {
      average: 'text-white text-xl font-semibold mb-4',
      label: 'text-white font-semibold mt-2 text-xl',
      gap: settings?.ratingPollScale === '5' ? 'gap-10' : 'gap-3',
      avgFontSize: 20
    }
  };
  const maxPercentage = Math.max(...contribution?.map(item => item.percentage) || [1]);
  return <div className='flex flex-col  justify-center items-center w-full h-full  ' >
    <p className={`text-white ${styles[origin as keyof typeof styles]?.average} `} style={{
      color: textColor,
      fontSize: fontScale ? fontScale * styles[origin as keyof typeof styles]?.avgFontSize : styles[origin as keyof typeof styles]?.avgFontSize,
      // @ts-expect-error
      ...fontFamily
    }}>
      Average {data?.averageRating || 0.0}
    </p>
    <div className={` h-full flex   justify-center  w-full  ${styles[origin as keyof typeof styles]?.gap}`}>
      {contribution?.map((bar, index: number) => {
        const relativeValue = maxPercentage > 0 ? (bar.percentage / maxPercentage) * 100 : 0;
        return (
          <Bar
            index={index}
            origin={origin}
            colors={colors}
            // @ts-expect-error
            fontFamily={fontFamily}
            value={bar?.percentage}
            relativeValue={relativeValue}
            label={bar.option_number?.toString() || ''}
            key={`rating-bar-${bar.option_number || index}`}
            audienceContributionCount={bar?.totalUsers || 0}
            settings={{ ...settings, ratingPollsFontSize: fontScale }}
            audienceProfile={bar?.users?.map((user) => user.username) || []}
          />
        );
      })}
    </div>

  </div>;
};

export default RatingPollsChart;
